import {
  formatCurrency,
  formatBillingInterval,
  formatDate,
} from '@chargify/formatters';
import { TFunction } from 'react-i18next';

import { SubscriptionModel } from '../models';

type SubscriptionDetails = Partial<SubscriptionModel> & {
  locale: string;
  t: TFunction<'common'[]>;
};

export type FormattedSubscriptionDetails = ReturnType<
  typeof useFormatSubscriptionDetails
>;

export const useFormatSubscriptionDetails = (
  subscriptionDetails: SubscriptionDetails
) => {
  const {
    status,
    currency,
    currentBillingAmountInCents,
    currentPeriodEndsAt,
    product: { priceInCents, trialPriceInCents, interval, intervalUnit },
    canceledAt,
    expiresAt,
    trialEndedAt,
    balanceInCents,
    nextAssessmentAt,
    automaticallyResumeAt,
    locale,
    scheduledCancellationAt,
    t,
  } = subscriptionDetails;

  const format = (
    value: string | number,
    type: 'currency' | 'date' | 'billingInterval'
  ) => {
    if (value === 0) {
      return formatCurrency(currency, value, locale);
    }
    if (!value) return;
    if (type === 'currency') {
      return formatCurrency(currency, value as number, locale);
    } else if (type === 'date') {
      return formatDate(value);
    }
  };

  const nextBillingAmount =
    status === 'cancellation_scheduled' ||
    status === 'active' ||
    status === 'plan_change_scheduled'
      ? balanceInCents + currentBillingAmountInCents
      : currentBillingAmountInCents;

  return {
    totalPrice: format(priceInCents, 'currency'),
    interval: formatBillingInterval(interval, intervalUnit, t),
    nextBillingAmount: format(nextBillingAmount, 'currency'),
    basePlanPrice: format(priceInCents, 'currency'),
    priceAfterTrial: format(currentBillingAmountInCents, 'currency'),
    trialPrice: format(trialPriceInCents, 'currency'),
    currentBalance: format(balanceInCents, 'currency'),
    nextBillingAttempt: format(nextAssessmentAt, 'date'),
    autoResumeDate: format(automaticallyResumeAt, 'date'),
    nextBillingDate: format(currentPeriodEndsAt, 'date'),
    cancellationDate: format(canceledAt, 'date'),
    expirationDate: format(expiresAt, 'date'),
    trialExpirationDate: format(trialEndedAt, 'date'),
    scheduledCancellationDate: format(scheduledCancellationAt, 'date'),
  };
};
